import React, {useMemo, useState} from "react";
import {ITgBot} from "../../../store/tgBots/types";
import TableContainer from "../../../components/Common/TableContainer";
import {Button} from "reactstrap";
import DeleteModal from "../../../components/Common/DeleteModal";
import usePermissions from "../../../hooks/usePermissions";
import {toast} from "react-toastify";

interface TgBotsTableProps {
    data: ITgBot[]
    pageSize: number
    onPageSizeChange?: (newPageSize: number) => void
    onSearchChange?: (search: string) => void
    onSortChange?: (sortKey?: string, sortOrder?: string) => void

    onUpdate?: (tgBotId: number) => void
    onRemove?: (tgBotId: number) => void
}

const customSort = [{id: "id", desc: true}];

const TableTgBots: React.FC<TgBotsTableProps> = ({
                                                     data,
                                                     pageSize,
                                                     onPageSizeChange,
                                                     onSearchChange,
                                                     onSortChange,
                                                     onUpdate,
                                                     onRemove
                                                 }) => {
    const {hasPermission} = usePermissions();

    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);

    const [revealedMap, setRevealedMap] = useState<Record<string, boolean>>({});

    const handleUpdateClick = (brokerData: ITgBot) => {
        if (onUpdate) onUpdate(brokerData.id)
    }

    const handleRemoveClick = (brokerData: ITgBot) => {
        setDeleteModal(true);
        setDeleteId(brokerData.id);
    }

    const handleSureDeleteClick = () => {
        if (deleteId && onRemove) onRemove(deleteId)

        setDeleteModal(false);
        setDeleteId(null);
    }

    const baseColumn = {
        filterable: true,
        disableFilters: true,
        sortable: true,
    };

    const handleRevealOrCopy = (key: string, value: string) => {
        setRevealedMap(prev => {
            if (!prev[key]) return {...prev, [key]: true};

            navigator.clipboard.writeText(value)
                .then(() => toast.success("Copied to clipboard"));

            return prev;
        });
    };

    function RevealCopyButton({value, revealed, onClick}: { value: string; revealed: boolean; onClick: () => void }) {
        return (
            <button className="btn btn-light py-0 px-1 text-nowrap" onClick={onClick}>
                {revealed ? value : <em className="opacity-75">Click to reveal</em>}
            </button>
        );
    }

    function BotTokenCell(row: any, botKey: string, tokenKey: string) {
        const data = row.original;
        const rowId = data.id;

        const buildKey = (id: number, key: string) => `${id}-${key}`

        return (
            <>
                <div className="mb-1">
                    <strong>Bot: </strong>
                    <RevealCopyButton
                        value={data[botKey]}
                        revealed={revealedMap[buildKey(rowId, botKey)]}
                        onClick={() => handleRevealOrCopy(buildKey(rowId, botKey), data[botKey])}
                    />
                </div>

                <div>
                    <strong>Token: </strong>
                    <RevealCopyButton
                        value={data[tokenKey]}
                        revealed={revealedMap[buildKey(rowId, tokenKey)]}
                        onClick={() => handleRevealOrCopy(buildKey(rowId, tokenKey), data[tokenKey])}
                    />
                </div>
            </>
        );
    }

    const columns = useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
                ...baseColumn,
            },
            {
                Header: "Name",
                accessor: "name",
                ...baseColumn,
            },
            {
                Header: "PR",
                Cell: ({row}: any) => BotTokenCell(row, "pr_bot", "pr_token"),
            },
            {
                Header: "CL",
                Cell: ({row}: any) => BotTokenCell(row, "cl_bot", "cl_token"),
            },
            {
                Header: "Error",
                Cell: ({row}: any) => BotTokenCell(row, "error_bot", "error_token"),
            },
            {
                Header: "Office",
                Cell: ({row}: any) => row.original.offices.map((o: any) => o.prefix.toUpperCase()).join(', '),
            },
            {
                Header: "Action",
                Cell: (cellProps: any) => {
                    return (
                        <div className="d-flex gap-3">
                            {hasPermission("update-telegram-bots") &&
                                <Button color="link" className="text-success p-0"
                                        onClick={() => handleUpdateClick(cellProps.row.original)}>
                                    <i className="mdi mdi-pencil font-size-18"></i>
                                </Button>}

                            {hasPermission("delete-telegram-bots") &&
                                <Button color="link" className="text-danger p-0"
                                        onClick={() => handleRemoveClick(cellProps.row.original)}>
                                    <i className="mdi mdi-delete font-size-18"></i>
                                </Button>}
                        </div>
                    );
                },
            },
        ],
        [hasPermission, revealedMap, handleRevealOrCopy]
    );

    return (
        <>
            <TableContainer
                columns={columns}
                data={data}
                isGlobalFilter={true}
                customPageSize={pageSize}
                className="table align-middle datatable dt-responsive table-check nowrap"
                onPageSizeChange={onPageSizeChange}
                onSearchChange={onSearchChange}
                onSortChange={onSortChange}
                customSort={customSort}
            />

            {deleteModal && <DeleteModal
                show={deleteModal}
                onDeleteClick={handleSureDeleteClick}
                onCloseClick={() => setDeleteModal(false)}
            />}
        </>
    )
}

export default TableTgBots;